import { usePage } from '@inertiajs/vue3'
import { computed } from 'vue'

import { ROLES } from '@/constants/roles'
import { ZONES } from '@/constants/zones'

/**
 * Auth state composable - wraps Inertia's shared `auth.user` prop.
 *
 * Two layers of helpers:
 *   - Role-level: `isRider`, `isPhotographer`, `isAdmin`, `hasRole(name)`.
 *     Use for narrow permission checks ("does this user have the rider role?").
 *   - Zone-level: `zones`, `hasZone(zone)`, `isInMultipleZones`. Use for layout
 *     decisions where several roles can share the same area (e.g. rider +
 *     future carEnthusiast → profile zone, so the zone-switcher stays generic).
 *
 * Prefer zone-level helpers in layouts/navigation code so adding new roles
 * only means mapping them to an existing zone - no UI refactor.
 */
export function useAuth() {
    const page = usePage()
    const user = computed(() => page.props.auth?.user ?? null)

    const hasRole = (role) => user.value?.roles?.includes(role) ?? false
    const can = (permission) => user.value?.permissions?.includes(permission) ?? false

    const isRider = computed(() => hasRole(ROLES.RIDER))
    const isCarEnthusiast = computed(() => hasRole(ROLES.CAR_ENTHUSIAST))
    const isPhotographer = computed(() => hasRole(ROLES.PHOTOGRAPHER))

    const zones = computed(() => user.value?.zones ?? [])
    const hasZone = (zone) => zones.value.includes(zone)

    return {
        user,
        isAuthenticated: computed(() => user.value !== null),
        // Role-level (fine-grained permission checks)
        isRider,
        isCarEnthusiast,
        isPhotographer,
        isAdmin: computed(() => hasRole(ROLES.ADMIN)),
        hasRole,
        can,
        // Zone-level (layout + navigation decisions)
        zones,
        hasZone,
        isInMultipleZones: computed(() => zones.value.length > 1),
    }
}

// Re-export so callers that want the zone constants alongside the composable
// don't need a second import.
export { ZONES }
